import React, { useState, useRef } from 'react';
import {
  Box,
  Flex,
  HStack,
  VStack,
  Heading,
  Text,
  Button,
  SimpleGrid,
  Container,
  Badge,
  Icon,
  Input,
  Textarea,
  FormControl,
  FormLabel,
  Divider,
  List,
  ListItem,
  ListIcon,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  useToast,
  Image,
} from '@chakra-ui/react';
import { CheckCircleIcon } from '@chakra-ui/icons';
import logoIcon from '../assets/logo/aplica-ia-icone.svg';
import logoHorizontal from '../assets/logo/aplica-ia-logo-horizontal.svg';
import { FiMessageCircle, FiZap, FiLink, FiShield, FiMic, FiSearch, FiDatabase, FiPlay } from 'react-icons/fi';
import { connectorIcon } from '../assets/connectors';
import { Link as RouterLink, Navigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { publicApi } from '../services/api';

const connectorLabel: Record<string, string> = {
  whatsapp: 'WhatsApp',
  sql: 'Banco SQL',
  sql_agent: 'Agente Local',
  s3: 'Amazon S3',
  google_drive: 'Google Drive',
  google_drive_managed: 'Google Drive',     // variante 1-clique → mesmo nome
  sharepoint: 'SharePoint',
  sharepoint_managed: 'SharePoint',
};

// Nomes amigáveis e SEM duplicar a variante "managed" (1-clique) do mesmo serviço.
const connectorNames = (types: string[]): string =>
  Array.from(new Set(types.map((c) => connectorLabel[c] || c))).join(', ');

const Feature = ({ icon, title, desc }: { icon: any; title: string; desc: string }) => (
  <VStack align="flex-start" spacing={2} p={5} borderWidth="1px" borderRadius="xl" bg="white" shadow="sm">
    <Icon as={icon} boxSize={6} color="brand.500" />
    <Text fontWeight="bold">{title}</Text>
    <Text fontSize="sm" color="gray.600">{desc}</Text>
  </VStack>
);

// Perguntas e respostas — mostra valor e tira dúvidas comuns do cliente.
const faq: Array<{ q: string; a: string }> = [
  {
    q: 'O que a Aplica IA faz?',
    a: 'Reúne todo o conhecimento da sua empresa — documentos, contratos, planilhas, treinamentos, relatórios, conversas de WhatsApp e até áudios de reuniões — num único ambiente, e deixa sua equipe perguntar e consultar com IA. As respostas sempre citam a fonte. Tudo controlado por você, com acesso por grupos e usuários.',
  },
  {
    q: 'Como dou acesso de IA aos meus funcionários de forma segura?',
    a: 'Você (administrador) cria grupos (ex.: Financeiro, Jurídico, Comercial) e define exatamente quais bases de conhecimento cada grupo ou pessoa pode acessar. Cada funcionário só enxerga e consulta o que foi liberado para ele — nada além disso. É IA para o time inteiro, sem abrir mão do controle.',
  },
  {
    q: 'Quais são os casos de uso?',
    a: 'Consolidar documentos e contratos importantes num só lugar; treinar a equipe com materiais e responder dúvidas internas (CNPJ, políticas, procedimentos); gerar relatórios e resumos; transcrever áudios de reuniões e criar atas automaticamente; consultar o histórico de negociações com um cliente; analisar editais e licitações; padronizar respostas do comercial. E muito mais — qualquer coisa que hoje está espalhada e dá trabalho de achar.',
  },
  {
    q: 'A IA pode trabalhar sozinha? (automações)',
    a: 'Sim. Você cria automações com gatilhos: quando algo acontece (nova mensagem de WhatsApp, novo arquivo), a IA processa o que você pediu e entrega o resultado no WhatsApp ou e-mail. Ex.: "chegou um novo contrato → resuma os riscos e mande pro jurídico" ou "cliente pediu proposta no WhatsApp → gere um rascunho e me envie".',
  },
  {
    q: 'De onde vêm os dados? Preciso subir tudo na mão?',
    a: 'Pode subir documentos manualmente, mas o forte é a ingestão automática: conecte WhatsApp, Google Drive, SharePoint, Amazon S3 e bancos de dados, e o conhecimento entra sozinho, todo dia. Áudios viram texto automaticamente.',
  },
  {
    q: 'É seguro? Meus dados ficam isolados?',
    a: 'Sim. Cada empresa fica totalmente isolada das demais; as credenciais das integrações são criptografadas; o acesso é controlado por grupos; e a IA só responde com base no que você autorizou. Pensado para LGPD.',
  },
  {
    q: 'A IA inventa respostas?',
    a: 'Não. Cada resposta cita a fonte (qual documento e qual trecho). Quando não há base para responder, ela diz que não encontrou — em vez de inventar.',
  },
  {
    q: 'Transcreve áudios de reunião e cria atas?',
    a: 'Sim. Áudios e notas de voz (inclusive do WhatsApp) são transcritos e viram conhecimento pesquisável — e a IA pode gerar a ata ou o resumo da reunião automaticamente.',
  },
  {
    q: 'Preciso instalar algo?',
    a: 'Não. É 100% web. Você se cadastra no plano grátis e usa direto no navegador.',
  },
  {
    q: 'Como começo e quanto custa?',
    a: 'Você começa no plano Grátis para sempre (com WhatsApp), sem cartão. Quando precisar de mais — conectores, mais usuários, modelos premium — assina Essencial, Profissional ou Empresarial (veja os valores na seção de planos acima) e muda de plano a qualquer momento.',
  },
];

const Landing = () => {
  const { isAuthenticated } = useAuth();
  const toast = useToast();
  const { data: plans = [] } = useQuery('public-plans', publicApi.plans, { staleTime: Infinity });
  const [contact, setContact] = useState({ name: '', email: '', company: '', message: '', phone: '' });
  const [sending, setSending] = useState(false);

  // Player do vídeo: começa mostrando o poster + botão de play (sem tela preta).
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoStarted, setVideoStarted] = useState(false);
  const startVideo = () => {
    setVideoStarted(true);
    videoRef.current?.play();
  };

  if (isAuthenticated) return <Navigate to="/inicio" replace />;

  const sendContact = async () => {
    if (!contact.name.trim() || !contact.email.trim() || !contact.message.trim()) {
      toast({ title: 'Preencha nome, e-mail e mensagem', status: 'warning', duration: 2500 });
      return;
    }
    setSending(true);
    try {
      await publicApi.contact(contact);
      toast({ title: 'Recebido! Entraremos em contato.', status: 'success', duration: 4000, isClosable: true });
      setContact({ name: '', email: '', company: '', message: '', phone: '' });
    } catch {
      toast({ title: 'Erro ao enviar. Tente novamente.', status: 'error', duration: 3000 });
    } finally {
      setSending(false);
    }
  };

  const fmtBrl = (v: number | null) => (v == null ? 'Sob consulta' : v === 0 ? 'Grátis' : `R$ ${v.toLocaleString('pt-BR')}`);

  return (
    <Box bg="gray.50" minH="100vh">
      {/* Navbar */}
      <Box bg="white" borderBottomWidth="1px" position="sticky" top={0} zIndex={10}>
        <Container maxW="6xl">
          <Flex h={16} align="center" justify="space-between">
            <HStack spacing={2}>
              <Image src={logoIcon} alt="Aplica IA" boxSize="36px" borderRadius="md" />
              <Heading size="md" color="brand.600">Aplica IA</Heading>
            </HStack>
            <HStack spacing={3}>
              <Button as="a" href="#faq" variant="ghost" size="sm" display={{ base: 'none', md: 'inline-flex' }}>Perguntas</Button>
              <Button as={RouterLink} to="/login" variant="ghost" size="sm">Entrar</Button>
              <Button as={RouterLink} to="/signup" colorScheme="brand" size="sm">Começar grátis</Button>
            </HStack>
          </Flex>
        </Container>
      </Box>

      {/* Hero */}
      <Container maxW="5xl" py={{ base: 12, md: 20 }} textAlign="center">
        <Badge colorScheme="brand" mb={4} px={3} py={1} borderRadius="full">Conhecimento da sua empresa + IA</Badge>
        <Heading size="2xl" lineHeight="1.1" mb={5}>
          Pare de caçar informação. <Text as="span" color="brand.600">Pergunte.</Text>
        </Heading>
        <Text fontSize="xl" color="gray.600" maxW="3xl" mx="auto" mb={8}>
          Conecte WhatsApp, drives, planilhas e documentos da sua empresa e converse com tudo num só lugar —
          com respostas que citam a fonte e não inventam.
        </Text>
        <HStack justify="center" spacing={4}>
          <Button as={RouterLink} to="/signup" colorScheme="brand" size="lg">Começar grátis</Button>
          <Button as="a" href="#planos" variant="outline" size="lg">Ver planos</Button>
        </HStack>

        {/* Vídeo promocional — hospedado no MinIO (fora do repo/bundle). Mostra um
            poster (capa) + botão de play; só baixa os ~24MB quando o usuário clica. */}
        <Box
          position="relative"
          mt={{ base: 10, md: 14 }}
          maxW="4xl"
          mx="auto"
          borderRadius="xl"
          overflow="hidden"
          shadow="2xl"
          borderWidth="1px"
          borderColor="gray.200"
          bg="black"
        >
          <video
            ref={videoRef}
            src="https://minio.celintelligence.com/aplicaia-public/video-promocional.mp4"
            poster="https://minio.celintelligence.com/aplicaia-public/video-poster-ia.jpg"
            controls={videoStarted}
            preload="none"
            playsInline
            style={{ display: 'block', width: '100%', aspectRatio: '16 / 9', objectFit: 'cover' }}
          />
          {/* Capa clicável com botão de play — some ao iniciar o vídeo. */}
          {!videoStarted && (
            <Flex
              position="absolute"
              inset={0}
              align="center"
              justify="center"
              cursor="pointer"
              onClick={startVideo}
              bg="blackAlpha.300"
              transition="background 0.2s"
              _hover={{ bg: 'blackAlpha.400' }}
              role="button"
              aria-label="Reproduzir vídeo"
            >
              <Flex
                align="center"
                justify="center"
                boxSize={{ base: '64px', md: '84px' }}
                borderRadius="full"
                bg="whiteAlpha.900"
                shadow="lg"
                transition="transform 0.2s"
                _groupHover={{ transform: 'scale(1.05)' }}
              >
                <Icon as={FiPlay} boxSize={{ base: 7, md: 9 }} color="brand.500" ml="4px" />
              </Flex>
            </Flex>
          )}
        </Box>
      </Container>

      {/* Como funciona */}
      <Container maxW="6xl" py={10}>
        <Heading size="lg" textAlign="center" mb={8}>Como funciona</Heading>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
          {[
            ['1. Conecte suas fontes', 'WhatsApp, Google Drive, SharePoint, S3, banco interno e uploads de documentos e áudios.'],
            ['2. O sistema indexa sozinho', 'Ingestão automática e diária. Áudios viram texto (transcrição). Nada manual.'],
            ['3. Pergunte e consulte', 'Converse com seu conhecimento. As respostas citam a fonte exata.'],
          ].map(([t, d]) => (
            <VStack key={t} align="flex-start" spacing={2} p={6} borderWidth="1px" borderRadius="xl" bg="white" shadow="sm">
              <Text fontWeight="bold" color="brand.600">{t}</Text>
              <Text fontSize="sm" color="gray.600">{d}</Text>
            </VStack>
          ))}
        </SimpleGrid>
      </Container>

      {/* Diferenciais */}
      <Container maxW="6xl" py={10}>
        <Heading size="lg" textAlign="center" mb={8}>Por que a Aplica IA</Heading>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={5}>
          <Feature icon={FiMessageCircle} title="WhatsApp vira conhecimento" desc="Conecte via QR e escolha quais conversas ingerir. Até notas de voz são transcritas." />
          <Feature icon={FiLink} title="Conecte tudo" desc="Google Drive, SharePoint, S3 e banco interno. Ingestão automática diária." />
          <Feature icon={FiSearch} title="Respostas com fonte" desc="Cada resposta cita de onde veio. Não alucina." />
          <Feature icon={FiMic} title="Áudio e documentos" desc="PDF, DOCX, planilhas, áudios e vídeos — tudo pesquisável." />
          <Feature icon={FiZap} title="Automações com IA" desc="Gatilho (nova mensagem/arquivo) → a IA processa → entrega no seu e-mail ou WhatsApp." />
          <Feature icon={FiShield} title="Seguro e isolado" desc="Cada cliente isolado, credenciais criptografadas, governança de uso e LGPD." />
        </SimpleGrid>
      </Container>

      {/* Integrações disponíveis — prova visual */}
      <Box bg="white" py={14}>
        <Container maxW="5xl">
          <Heading size="lg" textAlign="center" mb={2}>Conecte suas fontes</Heading>
          <Text textAlign="center" color="gray.600" mb={8}>
            Em poucos cliques. As credenciais ficam criptografadas e cada cliente é isolado.
          </Text>
          <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={6}>
            {[
              { type: 'google_drive', label: 'Google Drive' },
              { type: 'sharepoint', label: 'SharePoint' },
              { type: 's3', label: 'Amazon S3' },
              { type: 'sql', label: 'Banco SQL' },
              { type: 'whatsapp', label: 'WhatsApp' },
              { type: 'sql_agent', label: 'Agente Local' },
            ].map((c) => (
              <VStack key={c.type} spacing={2}>
                <Box
                  boxSize="64px"
                  borderWidth="1px"
                  borderRadius="xl"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  bg="gray.50"
                >
                  {connectorIcon(c.type)
                    ? <Image src={connectorIcon(c.type)} alt={c.label} boxSize="38px" objectFit="contain" />
                    : <FiDatabase size="28px" />}
                </Box>
                <Text fontSize="sm" color="gray.700" textAlign="center">{c.label}</Text>
              </VStack>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Planos */}
      <Box bg="white" py={14} id="planos">
        <Container maxW="6xl">
          <Heading size="lg" textAlign="center" mb={2}>Planos</Heading>
          <Text textAlign="center" color="gray.600" mb={10}>Comece grátis para sempre, sem cartão. Faça upgrade quando precisar de mais.</Text>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
            {plans.map((p) => {
              const isFree = !p.price_brl;
              const isPopular = p.id === 'profissional';
              return (
              <VStack
                key={p.id}
                align="stretch"
                spacing={4}
                p={6}
                borderWidth={isPopular ? '2px' : '1px'}
                borderColor={isPopular ? 'brand.400' : 'gray.200'}
                borderRadius="xl"
                bg="gray.50"
                position="relative"
              >
                {isPopular && (
                  <Badge colorScheme="brand" position="absolute" top={-3} left="50%" transform="translateX(-50%)" px={3} borderRadius="full">
                    Mais popular
                  </Badge>
                )}
                <Text fontWeight="bold" fontSize="lg">{p.label}</Text>
                <HStack align="baseline">
                  <Heading size="lg">{fmtBrl(p.price_brl)}</Heading>
                  {p.price_brl != null && p.price_brl > 0 && <Text color="gray.500" fontSize="sm">/mês</Text>}
                </HStack>
                <List spacing={2} fontSize="sm">
                  <ListItem><ListIcon as={CheckCircleIcon} color="green.500" />{p.max_users} {p.max_users > 1 ? 'usuários' : 'usuário'}</ListItem>
                  <ListItem><ListIcon as={CheckCircleIcon} color="green.500" />{p.max_collections >= 1000 ? 'Bases ilimitadas' : `${p.max_collections} ${p.max_collections > 1 ? 'bases de conhecimento' : 'base de conhecimento'}`}</ListItem>
                  <ListItem><ListIcon as={CheckCircleIcon} color="green.500" />Até {p.max_documents?.toLocaleString('pt-BR')} documentos</ListItem>
                  <ListItem><ListIcon as={CheckCircleIcon} color="green.500" />Arquivos de até {p.max_file_mb} MB</ListItem>
                  <ListItem><ListIcon as={CheckCircleIcon} color="green.500" />Conectores: {connectorNames(p.connectors)}</ListItem>
                  {isFree && p.max_whatsapp_messages && (
                    <ListItem><ListIcon as={CheckCircleIcon} color="green.500" />1 automação · {p.max_whatsapp_messages} mensagens/mês</ListItem>
                  )}
                  <ListItem><ListIcon as={CheckCircleIcon} color="green.500" />{p.models_label || (p.premium_models ? 'Modelos premium (Claude/Gemini Pro)' : 'Modelos padrão')}</ListItem>
                  {!isFree && (
                    <ListItem><ListIcon as={CheckCircleIcon} color="green.500" />{p.allow_audio ? 'Transcrição de áudio' : 'Sem áudio'}{p.allow_ocr ? ' · OCR' : ''}</ListItem>
                  )}
                </List>
                <Button as={RouterLink} to="/signup" colorScheme={isPopular ? 'brand' : 'gray'} mt={2}>
                  {isFree ? 'Começar grátis' : 'Assinar'}
                </Button>
              </VStack>
              );
            })}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Perguntas e respostas */}
      <Box bg="gray.50" py={14} id="faq">
        <Container maxW="3xl">
          <Heading size="lg" textAlign="center" mb={2}>Perguntas frequentes</Heading>
          <Text textAlign="center" color="gray.600" mb={8}>Tudo que você precisa saber antes de começar.</Text>
          <Accordion allowMultiple>
            {faq.map((item) => (
              <AccordionItem key={item.q} borderColor="gray.200">
                <AccordionButton py={4} _hover={{ bg: 'gray.100' }}>
                  <Box flex="1" textAlign="left" fontWeight="semibold">{item.q}</Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pb={4} color="gray.600" fontSize="sm">
                  {item.a}
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
          <Flex justify="center" mt={8}>
            <Button as={RouterLink} to="/signup" colorScheme="brand" size="lg">Começar grátis</Button>
          </Flex>
        </Container>
      </Box>

      {/* Contato / sob medida */}
      <Container maxW="3xl" py={14} id="contato">
        <VStack spacing={2} textAlign="center" mb={6}>
          <Heading size="lg">Precisa de algo sob medida?</Heading>
          <Text color="gray.600">Automações específicas, integrações próprias, implantação assistida. Fale com a gente.</Text>
        </VStack>
        <Box borderWidth="1px" borderRadius="xl" bg="white" p={6} shadow="sm">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <FormControl isRequired><FormLabel fontSize="sm">Nome</FormLabel>
              <Input size="sm" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} /></FormControl>
            <FormControl isRequired><FormLabel fontSize="sm">E-mail</FormLabel>
              <Input size="sm" type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} /></FormControl>
            <FormControl><FormLabel fontSize="sm">Empresa</FormLabel>
              <Input size="sm" value={contact.company} onChange={(e) => setContact({ ...contact, company: e.target.value })} /></FormControl>
            <FormControl><FormLabel fontSize="sm">WhatsApp/Telefone</FormLabel>
              <Input size="sm" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} /></FormControl>
          </SimpleGrid>
          <FormControl isRequired mt={4}><FormLabel fontSize="sm">Mensagem</FormLabel>
            <Textarea size="sm" rows={4} value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })} /></FormControl>
          <Button colorScheme="brand" mt={4} onClick={sendContact} isLoading={sending}>Enviar</Button>
        </Box>
      </Container>

      <Divider />
      <Container maxW="6xl" py={8}>
        <Flex justify="center" mb={6}>
          <Image src={logoHorizontal} alt="Aplica IA" maxW="280px" w="100%" borderRadius="lg" />
        </Flex>
        {/* Linkagem interna p/ SEO — páginas estáticas por segmento (recarga real). */}
        <Flex justify="center" wrap="wrap" gap={3} mb={5} fontSize="sm">
          <Text color="gray.500" fontWeight="semibold">Soluções:</Text>
          {[
            ['Contadores', '/para/contadores/'],
            ['Advogados', '/para/advogados/'],
            ['Imobiliárias', '/para/imobiliarias/'],
            ['Clínicas', '/para/clinicas/'],
            ['RH', '/para/recursos-humanos/'],
          ].map(([label, href]) => (
            <Button key={href} as="a" href={href} variant="link" size="sm" color="gray.500">{label}</Button>
          ))}
        </Flex>
        <Flex justify="space-between" align="center" direction={{ base: 'column', md: 'row' }} gap={3}>
          <Text fontSize="sm" color="gray.500">© Aplica IA — plataforma de conhecimento com IA.</Text>
          <HStack spacing={4} fontSize="sm">
            <Button as={RouterLink} to="/privacidade" variant="link" size="sm" color="gray.500">Privacidade</Button>
            <Button as={RouterLink} to="/termos" variant="link" size="sm" color="gray.500">Termos</Button>
            <Button as={RouterLink} to="/login" variant="link" size="sm">Entrar</Button>
            <Button as={RouterLink} to="/signup" variant="link" size="sm" colorScheme="brand">Começar grátis</Button>
          </HStack>
        </Flex>
      </Container>
    </Box>
  );
};

export default Landing;
